import { useState, useEffect } from "react";
import axios from "axios";
import { FileText, Download, Search, Filter, Clock, CheckCircle, XCircle, Edit2, Trash2, Eye, X, PlusSquare, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PurchaseOrders = () => {
  const { user, hasPerm } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editSupplier, setEditSupplier] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editItems, setEditItems] = useState([]);
  // New PO creation
  const [createDialog, setCreateDialog] = useState(false);
  const [newSupplier, setNewSupplier] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [newItems, setNewItems] = useState([]);
  const [inventoryList, setInventoryList] = useState([]);
  const [inventorySearch, setInventorySearch] = useState("");
  const [activePoItems, setActivePoItems] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);

  const isAdmin = user?.role === "admin";
  const canDownload = hasPerm("downloads_allowed");
  const canCreatePO = hasPerm("po_create");

  useEffect(() => { fetchOrders(); fetchActivePoItems(); }, [currentPage]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { applyFilters(); }, [orders, statusFilter, supplierFilter, dateFrom, dateTo]);

  const fetchOrders = async () => {
    try {
      const r = await axios.get(`${API_URL}/purchase-orders?page=${currentPage}&page_size=25`);
      setOrders(r.data.orders || r.data);
      setTotalPages(r.data.total_pages || 1);
      setTotalOrders(r.data.total || (r.data.orders || r.data).length);
    }
    catch (err) { console.error("Failed to fetch purchase orders:", err.message); toast.error("Failed to fetch purchase orders"); }
    finally { setLoading(false); }
  };

  const fetchActivePoItems = async () => {
    try { const r = await axios.get(`${API_URL}/purchase-orders/active-items`); setActivePoItems(r.data); }
    catch (err) { console.error("Failed to fetch active PO items:", err.message); }
  };

  const fetchInventory = async () => {
    try { const r = await axios.get(`${API_URL}/inventory?page=1&page_size=50000`); setInventoryList(r.data.items || []); }
    catch { toast.error("Failed to fetch inventory"); }
  };

  const applyFilters = () => {
    let f = [...orders];
    if (statusFilter && statusFilter !== "all") f = f.filter(po => po.status === statusFilter);
    if (supplierFilter) f = f.filter(po => po.supplier_name?.toLowerCase().includes(supplierFilter.toLowerCase()));
    if (dateFrom) { const d = new Date(dateFrom); f = f.filter(po => new Date(po.created_at) >= d); }
    if (dateTo) { const d = new Date(dateTo); d.setHours(23, 59, 59, 999); f = f.filter(po => new Date(po.created_at) <= d); }
    setFilteredOrders(f);
  };

  const handleExportPO = async (poId, format) => {
    try {
      const r = await axios.get(`${API_URL}/purchase-orders/${poId}/export/${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a'); link.href = url;
      const ext = format === 'excel' ? 'xlsx' : format;
      link.setAttribute('download', r.headers['content-disposition']?.split('filename=')[1] || `purchase_order.${ext}`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(error.response?.status === 403 ? "Download permission denied" : "Failed to export");
    }
  };

  const handleDeletePO = async (poId) => {
    if (!window.confirm("Delete this Purchase Order?")) return;
    try { await axios.delete(`${API_URL}/purchase-orders/${poId}`); toast.success("PO deleted"); fetchOrders(); fetchActivePoItems(); }
    catch { toast.error("Failed to delete PO"); }
  };

  const handleApprovePO = async (poId) => {
    try { await axios.post(`${API_URL}/purchase-orders/${poId}/approve`); toast.success("PO approved!"); setViewDialog(false); fetchOrders(); fetchActivePoItems(); }
    catch { toast.error("Failed to approve"); }
  };

  const handleRejectPO = async (poId) => {
    try { await axios.post(`${API_URL}/purchase-orders/${poId}/reject`); toast.success("PO rejected"); setViewDialog(false); fetchOrders(); fetchActivePoItems(); }
    catch { toast.error("Failed to reject"); }
  };

  const openViewDialog = (po) => {
    setSelectedPO(po); setEditing(false);
    setEditSupplier(po.supplier_name || ""); setEditNotes(po.notes || "");
    setEditItems(po.items.map(i => ({ ...i }))); setViewDialog(true);
  };

  const handleUpdatePO = async () => {
    try {
      await axios.put(`${API_URL}/purchase-orders/${selectedPO.id}`, { supplier_name: editSupplier.trim() || null, items: editItems, notes: editNotes || null });
      toast.success("PO updated!"); setEditing(false); setViewDialog(false); fetchOrders();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update PO"); }
  };

  const updateEditItem = (index, field, value) => {
    const u = [...editItems]; u[index][field] = field === 'quantity' ? parseInt(value) || 0 : value; setEditItems(u);
  };
  const removeEditItem = (index) => setEditItems(editItems.filter((_, i) => i !== index));

  // New PO creation
  const openCreateDialog = () => {
    setNewSupplier(""); setNewNotes(""); setNewItems([]); setInventorySearch("");
    fetchInventory(); setCreateDialog(true);
  };

  const addItemToPO = (item) => {
    if (newItems.find(i => i.part_number === item.part_number)) {
      toast.info("Item already added"); return;
    }
    setNewItems([...newItems, {
      part_number: item.part_number, name: item.name, brand: item.brand || null,
      category: item.category || null,
      quantity: Math.max(10, (item.reorder_level || 10) - item.quantity + 10),
      urgency: item.quantity === 0 ? "critical" : "high"
    }]);
  };

  const updateNewItem = (index, field, value) => {
    const u = [...newItems]; u[index][field] = field === 'quantity' ? parseInt(value) || 0 : value; setNewItems(u);
  };
  const removeNewItem = (index) => setNewItems(newItems.filter((_, i) => i !== index));

  const handleCreatePO = async () => {
    if (newItems.length === 0) { toast.error("Add at least one item"); return; }
    try {
      await axios.post(`${API_URL}/purchase-orders`, { supplier_name: newSupplier.trim() || null, items: newItems, notes: newNotes || null });
      toast.success(isAdmin ? "PO created and approved!" : "PO submitted for approval!");
      setCreateDialog(false); fetchOrders(); fetchActivePoItems();
    } catch (error) { toast.error("Failed to create PO"); }
  };

  const filteredInventory = inventoryList.filter(i =>
    !inventorySearch || i.name.toLowerCase().includes(inventorySearch.toLowerCase()) || i.part_number.toLowerCase().includes(inventorySearch.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const s = { pending: 'bg-yellow-100 text-yellow-800', approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800' };
    const icons = { pending: Clock, approved: CheckCircle, rejected: XCircle };
    const Icon = icons[status] || Clock;
    return <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase rounded-sm ${s[status] || s.pending}`}><Icon size={12} />{status}</span>;
  };

  const getUrgencyBadge = (u) => {
    const s = { critical: 'bg-red-600 text-white', high: 'bg-orange-600 text-white', medium: 'bg-yellow-600 text-white', low: 'bg-green-600 text-white' };
    return <span className={`px-2 py-1 text-xs font-bold uppercase rounded-sm ${s[u] || s.medium}`}>{u}</span>;
  };

  const DuplicateAlert = ({ partNumber }) => {
    const active = activePoItems[partNumber];
    if (!active || active.length === 0) return null;
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm ml-1" title={`Active PO: ${active.map(a => a.po_number).join(', ')}`}>
        <AlertTriangle size={10} /> PO exists
      </span>
    );
  };

  const stats = { total: orders.length, pending: orders.filter(o => o.status === 'pending').length, approved: orders.filter(o => o.status === 'approved').length, rejected: orders.filter(o => o.status === 'rejected').length };

  if (loading) return <div className="p-8 text-slate-500">Loading purchase orders...</div>;

  return (
    <div className="p-4 sm:p-6 md:p-8" data-testid="purchase-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl sm:text-4xl font-black text-slate-900 uppercase page-title" style={{ letterSpacing: '-0.02em' }}>Purchase Orders</h1>
          <p className="text-slate-500 mt-1 text-sm">Manage and track all purchase orders</p>
        </div>
        {canCreatePO && (
          <Button onClick={openCreateDialog} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm" data-testid="create-po-btn">
            <PlusSquare size={18} className="mr-2" /> New PO
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-sm p-4"><p className="text-xs text-slate-500 font-bold uppercase">Total</p><p className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</p></div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4"><p className="text-xs text-yellow-700 font-bold uppercase">Pending</p><p className="text-2xl font-bold text-yellow-700 mt-1">{stats.pending}</p></div>
        <div className="bg-green-50 border border-green-200 rounded-sm p-4"><p className="text-xs text-green-700 font-bold uppercase">Approved</p><p className="text-2xl font-bold text-green-700 mt-1">{stats.approved}</p></div>
        <div className="bg-red-50 border border-red-200 rounded-sm p-4"><p className="text-xs text-red-700 font-bold uppercase">Rejected</p><p className="text-2xl font-bold text-red-700 mt-1">{stats.rejected}</p></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-4 md:p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div><Label className="text-xs text-slate-600 font-bold uppercase">Status</Label><Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="mt-1 rounded-sm border-slate-300"><SelectValue placeholder="All" /></SelectTrigger><SelectContent className="bg-white"><SelectItem value="all">All</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent></Select></div>
          <div><Label className="text-xs text-slate-600 font-bold uppercase">Supplier</Label><div className="relative mt-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><Input value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)} placeholder="Search..." className="pl-9 rounded-sm border-slate-300" /></div></div>
          <div><Label className="text-xs text-slate-600 font-bold uppercase">From</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 rounded-sm border-slate-300" /></div>
          <div><Label className="text-xs text-slate-600 font-bold uppercase">To</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 rounded-sm border-slate-300" /></div>
          <div className="flex items-end"><Button onClick={() => { setStatusFilter("all"); setSupplierFilter(""); setDateFrom(""); setDateTo(""); }} variant="outline" className="w-full border-slate-300"><Filter size={16} className="mr-2" /> Clear</Button></div>
        </div>
        <p className="text-xs text-slate-500 mt-4">Showing {filteredOrders.length} of {orders.length}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        {filteredOrders.length === 0 ? (
          <div className="p-12 text-center"><FileText className="mx-auto text-slate-300 mb-4" size={64} /><p className="text-slate-500 text-lg">No purchase orders found</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="po-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">PO Number</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Supplier</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Created By</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase">Items</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Date</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredOrders.map((po) => (
                  <tr key={po.id} className="hover:bg-slate-50 transition-colors" data-testid={`po-row-${po.po_number}`}>
                    <td className="px-4 py-4 font-medium text-slate-900">{po.po_number}</td>
                    <td className="px-4 py-4 text-slate-700">{po.supplier_name || '-'}</td>
                    <td className="px-4 py-4"><p className="font-medium text-slate-900">{po.created_by_name}</p><p className="text-xs text-slate-500 capitalize">{po.created_by_role}</p></td>
                    <td className="px-4 py-4 text-center font-bold text-slate-900">{po.items.length}</td>
                    <td className="px-4 py-4 text-center">{getStatusBadge(po.status)}</td>
                    <td className="px-4 py-4 text-slate-600 text-sm">{new Date(po.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openViewDialog(po)} className="text-slate-600 hover:text-slate-900" title="View"><Eye size={16} /></Button>
                        {po.status === 'pending' && isAdmin && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => { openViewDialog(po); setEditing(true); }} className="text-blue-600 hover:text-blue-700" title="Edit"><Edit2 size={16} /></Button>
                            <Button size="sm" variant="ghost" onClick={() => handleApprovePO(po.id)} className="text-green-600 hover:text-green-700" title="Approve"><CheckCircle size={16} /></Button>
                            <Button size="sm" variant="ghost" onClick={() => handleRejectPO(po.id)} className="text-red-600 hover:text-red-700" title="Reject"><XCircle size={16} /></Button>
                          </>
                        )}
                        {po.status === 'approved' && canDownload && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => handleExportPO(po.id, 'excel')} className="text-green-600 hover:text-green-700" title="Excel"><Download size={16} /></Button>
                            <Button size="sm" variant="ghost" onClick={() => handleExportPO(po.id, 'pdf')} className="text-orange-600 hover:text-orange-700" title="PDF"><FileText size={16} /></Button>
                          </>
                        )}
                        {isAdmin && <Button size="sm" variant="ghost" onClick={() => handleDeletePO(po.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete"><Trash2 size={16} /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 bg-white border border-slate-200 rounded-sm p-3 gap-2">
          <p className="text-sm text-slate-500">Page {currentPage} of {totalPages} ({totalOrders} orders)</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(currentPage - 1)} data-testid="po-prev-page">Previous</Button>
            <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)} data-testid="po-next-page">Next</Button>
          </div>
        </div>
      )}

      {/* View/Edit PO Dialog */}
      <Dialog open={viewDialog} onOpenChange={(open) => { setViewDialog(open); if (!open) setEditing(false); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase flex items-center gap-3">
              {selectedPO?.po_number} {selectedPO && getStatusBadge(selectedPO.status)}
              {editing && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-sm normal-case font-medium">Editing</span>}
            </DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><p className="text-slate-500">Created By</p><p className="font-medium">{selectedPO.created_by_name}</p></div>
                <div><p className="text-slate-500">Created</p><p className="font-medium">{new Date(selectedPO.created_at).toLocaleDateString('en-IN')}</p></div>
                {selectedPO.approved_by_name && <><div><p className="text-slate-500">Approved By</p><p className="font-medium">{selectedPO.approved_by_name}</p></div><div><p className="text-slate-500">Approved</p><p className="font-medium">{new Date(selectedPO.approved_at).toLocaleDateString('en-IN')}</p></div></>}
              </div>
              {editing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>Supplier</Label><Input value={editSupplier} onChange={(e) => setEditSupplier(e.target.value)} data-testid="edit-po-supplier" /></div>
                    <div><Label>Notes</Label><Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={2} data-testid="edit-po-notes" /></div>
                  </div>
                  <div className="border rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100"><tr><th className="text-left px-3 py-2 text-xs font-bold uppercase">Part #</th><th className="text-left px-3 py-2 text-xs font-bold uppercase">Name</th><th className="text-center px-3 py-2 text-xs font-bold uppercase">Qty</th><th className="text-center px-3 py-2 text-xs font-bold uppercase">Urgency</th><th className="px-2 py-2"></th></tr></thead>
                      <tbody className="divide-y">
                        {editItems.map((item, i) => (
                          <tr key={item.id || item.part_number || `edit-${i}`}>
                            <td className="px-3 py-2 font-medium">{item.part_number}<DuplicateAlert partNumber={item.part_number} /></td>
                            <td className="px-3 py-2">{item.name}</td>
                            <td className="px-3 py-2"><Input type="number" min="1" value={item.quantity} onChange={(e) => updateEditItem(i, 'quantity', e.target.value)} className="w-20 text-center" /></td>
                            <td className="px-3 py-2 text-center"><Select value={item.urgency || 'medium'} onValueChange={(v) => updateEditItem(i, 'urgency', v)}><SelectTrigger className="w-28"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="critical">Critical</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent></Select></td>
                            <td className="px-2 py-2"><Button size="sm" variant="ghost" onClick={() => removeEditItem(i)} className="text-red-600"><X size={14} /></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div><p className="text-slate-500 text-sm">Supplier</p><p className="font-medium">{selectedPO.supplier_name || 'Not specified'}</p></div>
                  {selectedPO.notes && <div><p className="text-slate-500 text-sm">Notes</p><p className="text-slate-700">{selectedPO.notes}</p></div>}
                  <div className="border rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100"><tr><th className="text-left px-4 py-3 text-xs font-bold uppercase">#</th><th className="text-left px-4 py-3 text-xs font-bold uppercase">Part #</th><th className="text-left px-4 py-3 text-xs font-bold uppercase">Name</th><th className="text-center px-4 py-3 text-xs font-bold uppercase">Qty</th><th className="text-center px-4 py-3 text-xs font-bold uppercase">Urgency</th></tr></thead>
                      <tbody className="divide-y">
                        {selectedPO.items.map((item, i) => (
                          <tr key={item.id || item.part_number || `view-${i}`} className="hover:bg-slate-50"><td className="px-4 py-3 text-slate-500">{i + 1}</td><td className="px-4 py-3 font-medium">{item.part_number}<DuplicateAlert partNumber={item.part_number} /></td><td className="px-4 py-3">{item.name}</td><td className="px-4 py-3 text-center font-bold">{item.quantity}</td><td className="px-4 py-3 text-center">{getUrgencyBadge(item.urgency || 'medium')}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            {selectedPO?.status === 'pending' && isAdmin && (
              editing ? (
                <><Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button><Button onClick={handleUpdatePO} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-po-edit">Save</Button></>
              ) : (
                <><Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-po-btn"><Edit2 size={16} className="mr-2" /> Edit</Button><Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => handleRejectPO(selectedPO.id)}><XCircle size={16} className="mr-2" /> Reject</Button><Button onClick={() => handleApprovePO(selectedPO.id)} className="bg-green-600 hover:bg-green-700 text-white"><CheckCircle size={16} className="mr-2" /> Approve</Button></>
              )
            )}
            {selectedPO?.status === 'approved' && canDownload && (
              <><Button variant="outline" onClick={() => handleExportPO(selectedPO.id, 'csv')}>CSV</Button><Button variant="outline" onClick={() => handleExportPO(selectedPO.id, 'excel')}>Excel</Button><Button onClick={() => handleExportPO(selectedPO.id, 'pdf')} className="bg-orange-600 hover:bg-orange-700 text-white">PDF</Button></>
            )}
            <Button variant="outline" onClick={() => setViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create PO Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold uppercase">Create Purchase Order</DialogTitle></DialogHeader>
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Supplier (Optional)</Label><Input value={newSupplier} onChange={(e) => setNewSupplier(e.target.value)} placeholder="Supplier name" data-testid="new-po-supplier" /></div>
              <div><Label>Notes (Optional)</Label><Textarea value={newNotes} onChange={(e) => setNewNotes(e.target.value)} rows={2} placeholder="Notes" data-testid="new-po-notes" /></div>
            </div>

            {/* Inventory Picker */}
            <div>
              <Label className="mb-2 block text-sm font-bold uppercase text-slate-600">Add Items from Inventory</Label>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <Input value={inventorySearch} onChange={(e) => setInventorySearch(e.target.value)} placeholder="Search inventory by name or part number..." className="pl-9 rounded-sm border-slate-300" data-testid="inventory-picker-search" />
              </div>
              <div className="border rounded-sm max-h-72 overflow-y-auto">
                {filteredInventory.length === 0 ? (
                  <p className="text-center text-slate-400 py-4 text-sm">No items found</p>
                ) : (
                  filteredInventory.map((item) => {
                    const isLow = item.quantity <= (item.reorder_level || 0);
                    return (
                    <div key={item.id} className={`flex items-center justify-between px-3 py-2 border-b border-slate-100 hover:bg-slate-50 text-sm ${isLow ? 'bg-orange-50/50' : ''}`}>
                      <div>
                        <span className="font-medium text-slate-900">{item.part_number}</span>
                        <span className="text-slate-500 ml-2">{item.name}</span>
                        <span className={`ml-2 text-xs font-medium ${item.quantity === 0 ? 'text-red-600' : isLow ? 'text-orange-600' : 'text-green-600'}`}>
                          ({item.quantity} in stock)
                        </span>
                        {isLow && <span className="ml-1 text-xs bg-orange-100 text-orange-700 px-1 rounded">LOW</span>}
                        <DuplicateAlert partNumber={item.part_number} />
                      </div>
                      <Button size="sm" variant="outline" onClick={() => addItemToPO(item)} className="h-7 text-xs" disabled={newItems.some(i => i.part_number === item.part_number)}>
                        {newItems.some(i => i.part_number === item.part_number) ? "Added" : "Add"}
                      </Button>
                    </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Selected Items */}
            {newItems.length > 0 && (
              <div>
                <Label className="mb-2 block text-sm font-bold uppercase text-slate-600">PO Items ({newItems.length})</Label>
                <div className="border rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100"><tr><th className="text-left px-3 py-2 text-xs font-bold uppercase">Part #</th><th className="text-left px-3 py-2 text-xs font-bold uppercase">Name</th><th className="text-center px-3 py-2 text-xs font-bold uppercase">Qty</th><th className="text-center px-3 py-2 text-xs font-bold uppercase">Urgency</th><th className="px-2 py-2"></th></tr></thead>
                    <tbody className="divide-y">
                      {newItems.map((item, i) => (
                        <tr key={item.part_number || `new-${i}`}>
                          <td className="px-3 py-2 font-medium">{item.part_number}<DuplicateAlert partNumber={item.part_number} /></td>
                          <td className="px-3 py-2">{item.name}</td>
                          <td className="px-3 py-2"><Input type="number" min="1" value={item.quantity} onChange={(e) => updateNewItem(i, 'quantity', e.target.value)} className="w-20 text-center" /></td>
                          <td className="px-3 py-2 text-center"><Select value={item.urgency} onValueChange={(v) => updateNewItem(i, 'urgency', v)}><SelectTrigger className="w-28"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="critical">Critical</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent></Select></td>
                          <td className="px-2 py-2"><Button size="sm" variant="ghost" onClick={() => removeNewItem(i)} className="text-red-600"><X size={14} /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!isAdmin && newItems.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                <p className="text-sm text-blue-800"><strong>Note:</strong> This PO will be submitted for Admin approval.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreatePO} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="submit-new-po" disabled={newItems.length === 0}>
              {isAdmin ? 'Create & Approve' : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrders;
